import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, resolveMedia } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, Search, Loader2, Image as ImageIcon, Trash2, Link2, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["ALL", "BRAND", "TEAM", "COMMUNITY", "FACILITY", "TRANSPORTATION", "SERVICES", "EVENTS", "GALLERY", "WEBSITE", "OTHER"];
const STATUSES = ["ALL", "APPROVED", "REVIEW", "ARCHIVED"];

export default function MediaLibrary() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("media:manage");
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ search: "", category: "ALL", status: "ALL", sort: "newest" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [usage, setUsage] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.append(k, v));
    try { const { data } = await api.get(`/media?${p.toString()}`); setItems(data); }
    catch (e) { toast.error(apiError(e)); setItems([]); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (asset) => {
    setDetail({ ...asset });
    try { const { data } = await api.get(`/media/${asset.id}/usage`); setUsage(data); } catch { setUsage([]); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "OTHER");
      try { await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (err) { toast.error(`${file.name}: ${apiError(err)}`); }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} file(s)`);
    setUploadOpen(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const saveDetail = async () => {
    try {
      await api.put(`/media/${detail.id}`, {
        title: detail.title, altText: detail.altText, description: detail.description,
        category: detail.category, status: detail.status,
      });
      toast.success("Saved"); setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async (force) => {
    try {
      await api.delete(`/media/${deleteTarget.id}${force ? "?force=true" : ""}`);
      toast.success("Archived"); setDeleteTarget(null); setDetail(null); load();
    } catch (e) {
      if (e?.response?.status === 409) { /* handled by confirm dialog text */ toast.error(apiError(e)); }
      else toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="media-library">
      <PageHeader
        title="Media Library"
        subtitle="Official Love For Mankind imagery, migrated assets, and uploads. Assets with unclear licensing are marked REVIEW."
        actions={canManage && (
          <Button onClick={() => setUploadOpen(true)} className="rounded-md" data-testid="media-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload
          </Button>
        )}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search title, alt text…" className="pl-9 bg-card" data-testid="media-search" />
        </div>
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger className="w-[170px] bg-card" data-testid="media-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-[150px] bg-card" data-testid="media-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
          <SelectTrigger className="w-[140px] bg-card" data-testid="media-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="name">Name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {items === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-muted-foreground"><ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" /> No media found.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
          {items.map((a) => (
            <button key={a.id} onClick={() => openDetail(a)} data-testid="media-item"
              className="group text-left border border-border rounded-lg bg-card overflow-hidden hover:border-primary/40 transition-colors">
              <div className="aspect-[4/3] bg-muted overflow-hidden relative">
                <img src={resolveMedia(a.thumbnail || a.url)} alt={a.altText || a.title} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
                <div className="absolute top-2 left-2"><StatusBadge value={a.status} /></div>
                {a.usageCount > 0 && (
                  <div className="absolute bottom-2 left-2 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> {a.usageCount}
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <p className="text-sm font-medium truncate">{a.title}</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">{a.category}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Media</DialogTitle></DialogHeader>
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">Select one or more images (JPG, PNG, WEBP, GIF, SVG, PDF)</p>
            <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" onChange={onUpload} className="hidden" id="media-file-input" data-testid="media-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-choose-files">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Choose files
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="truncate">{detail.title}</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="rounded-lg overflow-hidden border border-border bg-muted">
                  <img src={resolveMedia(detail.url)} alt={detail.altText} className="w-full object-contain max-h-72" />
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Title</Label><Input value={detail.title || ""} disabled={!canManage} onChange={(e) => setDetail({ ...detail, title: e.target.value })} data-testid="detail-title" /></div>
                  <div className="space-y-1.5"><Label>Alt Text</Label><Input value={detail.altText || ""} disabled={!canManage} onChange={(e) => setDetail({ ...detail, altText: e.target.value })} data-testid="detail-alt" /></div>
                  <div className="space-y-1.5"><Label>Description</Label><Textarea value={detail.description || ""} disabled={!canManage} onChange={(e) => setDetail({ ...detail, description: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5"><Label>Category</Label>
                      <Select value={detail.category} disabled={!canManage} onValueChange={(v) => setDetail({ ...detail, category: v })}>
                        <SelectTrigger data-testid="detail-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.filter((c) => c !== "ALL").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Status</Label>
                      <Select value={detail.status} disabled={!canManage} onValueChange={(v) => setDetail({ ...detail, status: v })}>
                        <SelectTrigger data-testid="detail-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.filter((c) => c !== "ALL").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Source: <span className="font-medium">{detail.source}</span> · Folder: {detail.folder}
                  </div>
                  <div className="border-t border-border pt-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Used On</p>
                    {usage.length === 0 ? <p className="text-sm text-muted-foreground">Not currently used.</p>
                      : usage.map((u) => <div key={u.id} className="text-sm">Used on: <span className="font-medium">{u.location}</span></div>)}
                  </div>
                </div>
              </div>
              {canManage && (
                <DialogFooter className="gap-2">
                  <Button variant="outline" className="text-danger border-danger/30 mr-auto" onClick={() => setDeleteTarget(detail)} data-testid="detail-delete">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Archive
                  </Button>
                  <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                  <Button onClick={saveDetail} data-testid="detail-save">Save changes</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this media?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.usageCount > 0
                ? `This asset is used in ${deleteTarget.usageCount} place(s). Archiving anyway may leave gaps on the website.`
                : "This asset will be archived and hidden from the library."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDelete(true)} className="bg-danger hover:bg-danger/90" data-testid="confirm-media-delete">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
